import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FolderOpen,
  Headphones,
  ClipboardList,
  ShoppingCart,
  Camera,
  Users,
  ChevronRight,
  ArrowUpRight,
  LogIn,
  Activity,
  BellRing,
  LineChart,
  FileSpreadsheet,
  Sparkles,
  Wrench,
  CheckCircle2,
} from "lucide-react";
import SEO from "../components/SEO";

/**
 * /portal — Client Portal marketing / preview page.
 *
 * Purpose of the actual portal (salesflow.hidroverse.co.za):
 *   • Live plant performance — daily log sheets, instant out-of-spec alerts,
 *     real-time trend charts, uptime reports and board-ready graphs
 *   • Support tickets — routed straight to the HidroVerse back office
 *   • Job cards — every site visit, signed and timestamped, with photo/video evidence
 *   • Documents — sales orders, quotes, invoices, manuals, warranties
 *   • Equipment-aware ordering — consumables / parts for installed kit
 *   • Site media — photos & videos uploaded by technicians as work happens
 *   • Team access — clients add their own users with per-role permissions
 */

const PORTAL_URL = "https://salesflow.hidroverse.co.za";

const SHOTS = {
  homeLight:
    "https://customer-assets.emergentagent.com/job_colors-25/artifacts/h47h26ch_image-1%20%283%29.png",
  homeDark:
    "https://customer-assets.emergentagent.com/job_colors-25/artifacts/iccn0c8p_image-1780323421154.png",
  documents:
    "https://customer-assets.emergentagent.com/job_colors-25/artifacts/7omebqyu_image-1%20%282%29.png",
  ticket:
    "https://customer-assets.emergentagent.com/job_colors-25/artifacts/jkc5qm2u_image-1%20%285%29.png",
  order:
    "https://customer-assets.emergentagent.com/job_colors-25/artifacts/c2q4rgh6_image-1.png",
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };

const CAPABILITIES = [
  {
    icon: Activity,
    title: "Live plant performance",
    body:
      "Operators log daily plant readings straight from the plant floor on phone or tablet — every parameter, every RO train, every dosing line. Managers see the entire site health in real time from anywhere.",
  },
  {
    icon: BellRing,
    title: "Instant out-of-spec alerts",
    body:
      "The moment a parameter falls outside its target window — TDS drift, pressure spike, recovery drop — the portal flags it and pings the responsible engineer. No waiting for the monthly walk-through.",
  },
  {
    icon: LineChart,
    title: "Real-time reports & board-ready charts",
    body:
      "Draw a trend chart across any parameters and date ranges in seconds. Export a Plant Uptime PDF, a compliance report or a boardroom slide directly from the portal — HidroVerse-branded, print-ready.",
  },
  {
    icon: Headphones,
    title: "Support tickets",
    body:
      "Log a ticket with photos or video straight from your phone. It lands in the HidroVerse back office tagged as priority — same channel our own technicians use.",
  },
  {
    icon: ClipboardList,
    title: "Job cards & site evidence",
    body:
      "Every site visit by a HidroVerse technician — what was done, what was replaced, what was billed, every photo taken on site. Signed off, timestamped and filed against your install.",
  },
  {
    icon: FolderOpen,
    title: "Documents",
    body:
      "Every sales order, quote, invoice, contract, manual, warranty and SHEQ permit filed against your account. Searchable across every folder.",
  },
  {
    icon: ShoppingCart,
    title: "Order from your installed equipment",
    body:
      "The storefront only shows parts and consumables that match the kit installed at your site. Reorder a cartridge, a seal, a dosing line — no part-number guessing.",
  },
  {
    icon: Camera,
    title: "Site photos & videos",
    body:
      "Technicians upload plant photos and short video clips to your account as they work. Your accounts team sees the same evidence we sign off on.",
  },
  {
    icon: Users,
    title: "Team access with permissions",
    body:
      "Invite your maintenance crew, your accounts department, your lodge GM — and give each one access only to the folders and modules they need.",
  },
];

const VIEWS = [
  {
    src: SHOTS.homeLight,
    label: "Home",
    title: "One screen — your whole account",
    body:
      "Active jobs, open tickets, documents on file, upcoming services. Plus four direct lines to the HidroVerse team: log a ticket, place an order, WhatsApp, or call.",
  },
  {
    src: SHOTS.documents,
    label: "Documents",
    title: "Every file, every folder — searchable",
    body:
      "Contracts & SLAs, proposals, manuals, datasheets, warranties, account docs, notices, site plans, SHEQ compliance. Each folder a permission boundary you control.",
  },
  {
    src: SHOTS.ticket,
    label: "Support",
    title: "Log a ticket in under a minute",
    body:
      "Subject, description, photos or a short video — submit. The ticket opens inside HidroVerse with your site, your install file and your contact already attached.",
  },
  {
    src: SHOTS.order,
    label: "Orders",
    title: "Reorder only what fits your site",
    body:
      "Search across the equipment registered against your site — every pump, every cartridge, every dosing line. Can't find a part? Describe it and we will source it.",
  },
];

const STEPS = [
  [
    "01",
    "We commission your site",
    "On handover, your portal is provisioned with the install file, equipment register, photos and the contract on file. Your customer ID lives at the top.",
  ],
  [
    "02",
    "Documents land as we transact",
    "Quotes, sales orders, invoices, job cards, datasheets and SHEQ paperwork are filed against your account as they are issued. Nothing lives in someone's inbox.",
  ],
  [
    "03",
    "You invite your team",
    "Add your accounts department, your maintenance crew, your GM — and set per-folder permissions. The accountant sees invoices; the maintenance lead sees job cards and tickets.",
  ],
];

export default function CustomerPortal() {
  return (
    <div data-testid="portal-page">
      <SEO
        title="Sign In"
        description="Live plant performance monitoring, instant out-of-spec alerts, real-time trend reports, support tickets, job cards and equipment-aware ordering. The HidroVerse client portal connects your site directly to our back office."
        path="/portal"
      />

      {/* HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-32 pb-14 md:pb-20"
        style={{ background: "#0C1116", color: "#F5F7FA" }}
        data-testid="portal-hero"
      >
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-5"
            initial="hidden"
            animate="show"
            variants={stagger}
          >
            <motion.div
              variants={fadeUp}
              className="mb-6 text-[0.84rem] font-semibold"
              style={{ color: "rgba(245,247,250,0.55)" }}
            >
              Client Portal
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-display"
              style={{
                fontSize: "clamp(2.4rem, 5.6vw, 4.4rem)",
                lineHeight: 1.04,
                letterSpacing: "-0.02em",
                fontWeight: 400,
                color: "#F5F7FA",
              }}
            >
              Your plant.<br />
              <span style={{ color: "#1FB6C1" }}>Live — every parameter, every ticket, every site visit.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-7 max-w-xl text-[1.02rem] font-light leading-relaxed"
              style={{ color: "rgba(245,247,250,0.78)" }}
            >
              The HidroVerse client portal is a live monitoring layer over your plant. Operators log daily
              readings from the plant floor on phone or tablet; managers see every parameter and every RO train
              in real time. Out-of-spec values raise instant alerts. Trend charts, uptime reports and
              board-ready graphs export in seconds. Support tickets, job cards, invoices and manuals live in the
              same account.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-9 flex flex-wrap gap-3">
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 font-semibold transition-transform hover:scale-[1.02]"
                style={{
                  background: "#1FB6C1",
                  color: "#070A0D",
                  fontSize: "0.78rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  borderRadius: 999,
                }}
                data-testid="portal-signin-cta"
              >
                <LogIn size={14} aria-hidden /> Sign in to portal
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 font-semibold transition-colors"
                style={{
                  border: "1px solid rgba(245,247,250,0.22)",
                  color: "#F5F7FA",
                  fontSize: "0.78rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  borderRadius: 999,
                }}
                data-testid="portal-request-access"
              >
                Request access
              </Link>
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mt-6 font-mono"
              style={{ fontSize: "0.72rem", letterSpacing: "0.04em", color: "rgba(245,247,250,0.42)" }}
            >
              salesflow.hidroverse.co.za · invite-only · provisioned at install
            </motion.p>
          </motion.div>

          {/* Hero screenshot — dark variant for the dark hero */}
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <div
              className="rounded-md overflow-hidden relative"
              style={{
                border: "1px solid rgba(31,182,193,0.22)",
                boxShadow:
                  "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
                background: "#0A0E13",
              }}
              data-testid="portal-hero-shot"
            >
              <img
                src={SHOTS.homeDark}
                alt="HidroVerse client portal — home dashboard"
                className="block w-full h-auto"
                loading="eager"
              />
              <div
                className="absolute top-3 left-3 px-2.5 py-1 font-mono"
                style={{
                  fontSize: "0.62rem",
                  letterSpacing: "0.16em",
                  color: "#1FB6C1",
                  background: "rgba(31,182,193,0.10)",
                  border: "1px solid rgba(31,182,193,0.35)",
                  borderRadius: 4,
                  textTransform: "uppercase",
                }}
              >
                live portal · home
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PERFORMANCE MONITORING — hero-adjacent "top feature" showcase ─── */}
      <PerformanceMonitoringSection />

      {/* JOB CARDS + SITE ORDERING mockups ─────────────────────────────── */}
      <OpsOrderingSection />

      {/* CAPABILITY GRID ──────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="portal-capabilities"
      >
        <div className="max-w-[1400px] mx-auto">
          <div
            className="mb-6 text-[0.82rem] font-semibold"
            style={{ color: "rgba(12,42,64,0.55)" }}
          >
            What the portal actually does
          </div>
          <h2
            className="font-display mb-12 max-w-3xl"
            style={{
              fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              fontWeight: 500,
              color: "#0C2A40",
            }}
          >
            Live monitoring, support, and operations — connected to our back office.
          </h2>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px"
            style={{ background: "rgba(0,0,0,0.08)" }}
          >
            {CAPABILITIES.map(({ icon: Icon, title, body }) => (
              <motion.div
                key={title}
                variants={fadeUp}
                style={{ background: "#FFFFFF", padding: "2rem 1.8rem" }}
                data-testid={`portal-cap-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className="w-11 h-11 rounded-md flex items-center justify-center mb-5"
                  style={{ background: "rgba(31,182,193,0.10)", color: "#1FB6C1" }}
                >
                  <Icon size={20} />
                </div>
                <div
                  className="font-display mb-3"
                  style={{
                    fontSize: "1.08rem",
                    color: "#0C2A40",
                    fontWeight: 600,
                    lineHeight: 1.25,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {title}
                </div>
                <p
                  className="font-light text-[0.92rem] leading-relaxed"
                  style={{ color: "rgba(26,31,37,0.72)" }}
                >
                  {body}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SCREEN TOUR ──────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#FAFAF7", color: "#0C2A40" }}
        data-testid="portal-views"
      >
        <div className="max-w-[1400px] mx-auto">
          <div
            className="mb-6 text-[0.82rem] font-semibold"
            style={{ color: "rgba(12,42,64,0.55)" }}
          >
            Inside the portal
          </div>
          <h2
            className="font-display mb-14 max-w-3xl"
            style={{
              fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              fontWeight: 500,
              color: "#0C2A40",
            }}
          >
            Four screens our clients actually use.
          </h2>

          <div className="grid gap-10 md:gap-14">
            {VIEWS.map((v, i) => {
              const reverse = i % 2 === 1;
              return (
                <motion.div
                  key={v.label}
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
                  className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                  data-testid={`portal-view-${v.label.toLowerCase()}`}
                >
                  <div className="lg:col-span-7">
                    <div
                      className="rounded-md overflow-hidden relative"
                      style={{
                        border: "1px solid rgba(12,42,64,0.10)",
                        boxShadow:
                          "0 20px 50px rgba(12,42,64,0.10), 0 0 0 1px rgba(12,42,64,0.04) inset",
                        background: "#FFFFFF",
                      }}
                    >
                      <img
                        src={v.src}
                        alt={`HidroVerse client portal — ${v.title}`}
                        className="block w-full h-auto"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="lg:col-span-5">
                    <div
                      className="mb-3 font-mono"
                      style={{
                        fontSize: "0.7rem",
                        letterSpacing: "0.18em",
                        color: "#1FB6C1",
                        textTransform: "uppercase",
                      }}
                    >
                      {v.label}
                    </div>
                    <h3
                      className="font-display mb-4"
                      style={{
                        fontSize: "clamp(1.3rem, 2.4vw, 1.8rem)",
                        lineHeight: 1.2,
                        letterSpacing: "-0.01em",
                        fontWeight: 500,
                        color: "#0C2A40",
                      }}
                    >
                      {v.title}
                    </h3>
                    <p
                      className="font-light text-[0.98rem] leading-relaxed"
                      style={{ color: "rgba(26,31,37,0.74)" }}
                    >
                      {v.body}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#FFFFFF", color: "#0C2A40" }}
        data-testid="portal-how"
      >
        <div className="max-w-[1300px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div
              className="mb-5 text-[0.82rem] font-semibold"
              style={{ color: "rgba(12,42,64,0.55)" }}
            >
              How it gets onto your phone
            </div>
            <h2
              className="font-display"
              style={{
                fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
                fontWeight: 500,
                color: "#0C2A40",
              }}
            >
              The portal is provisioned at install.<br />
              <span style={{ color: "#1FB6C1" }}>You add your team yourself.</span>
            </h2>
          </div>

          <div className="lg:col-span-7 grid gap-3">
            {STEPS.map(([no, t, b]) => (
              <div
                key={no}
                className="p-6 md:p-7 flex gap-5 items-start"
                style={{
                  background: "#FAFAF7",
                  border: "1px solid rgba(12,42,64,0.08)",
                  borderRadius: 4,
                }}
              >
                <div
                  className="font-mono shrink-0"
                  style={{
                    fontSize: "0.78rem",
                    letterSpacing: "0.16em",
                    color: "#1FB6C1",
                    fontWeight: 700,
                  }}
                >
                  {no}
                </div>
                <div>
                  <div
                    className="font-display mb-2"
                    style={{
                      fontSize: "1.02rem",
                      color: "#0C2A40",
                      fontWeight: 600,
                      letterSpacing: "-0.005em",
                    }}
                  >
                    {t}
                  </div>
                  <p
                    className="font-light text-[0.92rem] leading-relaxed"
                    style={{ color: "rgba(26,31,37,0.72)" }}
                  >
                    {b}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA ──────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 lg:px-20 py-16 md:py-20 text-center"
        style={{ background: "#1F2227", color: "#F5F7FA" }}
        data-testid="portal-closing"
      >
        <div className="max-w-[900px] mx-auto">
          <h2
            className="font-display mb-7"
            style={{
              fontSize: "clamp(1.8rem, 4vw, 3rem)",
              lineHeight: 1.1,
              letterSpacing: "-0.015em",
              fontWeight: 400,
              color: "#F5F7FA",
            }}
          >
            Already a client?<br />
            <span style={{ color: "#1FB6C1" }}>Your portal is waiting.</span>
          </h2>
          <div className="flex flex-wrap gap-3 justify-center mt-8">
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3 font-semibold transition-transform hover:scale-[1.02]"
              style={{
                background: "#1FB6C1",
                color: "#070A0D",
                fontSize: "0.78rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                borderRadius: 999,
              }}
              data-testid="portal-final-signin"
            >
              <LogIn size={14} aria-hidden /> Sign in <ArrowUpRight size={14} aria-hidden />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 font-semibold transition-colors"
              style={{
                border: "1px solid rgba(245,247,250,0.22)",
                color: "#F5F7FA",
                fontSize: "0.78rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                borderRadius: 999,
              }}
              data-testid="portal-final-cta"
            >
              Not yet — discuss your brief <ChevronRight size={14} aria-hidden />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------
//  PERFORMANCE MONITORING SECTION
//  Marketed as the top / most-valuable capability of the portal.
//  Uses inline SVG + HTML mockups (no external screenshots) so the
//  page carries zero client references. Everything below is a generic
//  "Water Treatment Plant" mock — no TGL, no Thornybush, no client name.
// -----------------------------------------------------------------------

const PERF_HIGHLIGHTS = [
  {
    icon: FileSpreadsheet,
    title: "Daily log sheets on any device",
    body:
      "Operators capture every plant reading — pressures, flows, TDS, recoveries, tank levels — from phone, tablet or plant PC. No paperwork, no lost sheets, no re-typing at month-end.",
  },
  {
    icon: BellRing,
    title: "Instant out-of-spec alerts",
    body:
      "Every parameter has a target window. The moment a reading drifts, the portal flags it and pings the responsible engineer — so action happens in hours, not on the next monthly walk-through.",
  },
  {
    icon: LineChart,
    title: "Real-time trend charts",
    body:
      "Overlay any parameters across any date ranges — up to 30-day windows. Spot drift on a membrane, a fouling curve on a filter or a pattern in raw-water quality before it becomes a fault.",
  },
  {
    icon: Sparkles,
    title: "Board-ready exports",
    body:
      "One click and the portal generates a Plant Performance PDF, a Trend Chart PDF, a Plant Uptime Report — all HidroVerse-branded, ready for management, boards or compliance auditors.",
  },
];

function PerformanceMonitoringSection() {
  return (
    <section
      className="px-6 md:px-12 lg:px-20 py-14 md:py-24 relative overflow-hidden"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="portal-performance"
    >
      {/* subtle cyan wash to make this section feel like the "top feature" */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(80% 60% at 50% 0%, rgba(31,182,193,0.08) 0%, rgba(31,182,193,0) 60%)",
        }}
      />
      <div className="max-w-[1400px] mx-auto relative">
        <div
          className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
          style={{
            background: "rgba(31,182,193,0.10)",
            border: "1px solid rgba(31,182,193,0.28)",
            color: "#0C2A40",
            fontSize: "0.66rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
          }}
          data-testid="portal-performance-badge"
        >
          <Activity size={12} aria-hidden style={{ color: "#1FB6C1" }} />
          Live plant monitoring · top feature
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left column — copy + value props */}
          <div className="lg:col-span-5">
            <h2
              className="font-display mb-5"
              style={{
                fontSize: "clamp(1.9rem, 3.6vw, 2.9rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                fontWeight: 500,
                color: "#0C2A40",
              }}
            >
              Your plant, on-line.<br />
              <span style={{ color: "#1FB6C1" }}>
                Every reading, every alert, every trend.
              </span>
            </h2>
            <p
              className="font-light text-[1rem] leading-relaxed mb-8"
              style={{ color: "rgba(26,31,37,0.72)", maxWidth: "48ch" }}
            >
              The most powerful part of the HidroVerse portal is not the paperwork —
              it is the live monitoring layer above the paperwork. Operators log
              readings from the plant floor. Managers see the entire site in real
              time. Board reports draw themselves.
            </p>

            <ul className="space-y-5" data-testid="portal-performance-highlights">
              {PERF_HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: "rgba(31,182,193,0.12)", color: "#1FB6C1" }}
                  >
                    <Icon size={18} aria-hidden />
                  </div>
                  <div>
                    <div
                      className="font-display mb-1.5"
                      style={{
                        fontSize: "1.02rem",
                        color: "#0C2A40",
                        fontWeight: 600,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {title}
                    </div>
                    <p
                      className="font-light text-[0.92rem] leading-relaxed"
                      style={{ color: "rgba(26,31,37,0.72)" }}
                    >
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right column — mockups */}
          <div className="lg:col-span-7 space-y-5">
            <PerfKpiRow />
            <PerfParameterTable />
            <div className="grid md:grid-cols-2 gap-5">
              <PerfTrendChart />
              <PerfUptimeCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -- KPI row --------------------------------------------------------------
function PerfKpiRow() {
  const cards = [
    { label: "OK", value: "149", color: "#1F9D63" },
    { label: "Warn", value: "4",  color: "#C99A2B" },
    { label: "Alarm", value: "5",  color: "#B85C38" },
    { label: "Completion", value: "66%", color: "#0C2A40" },
    { label: "Days",  value: "6",  color: "#0C2A40" },
    { label: "Params", value: "35", color: "#0C2A40" },
  ];
  return (
    <div
      className="rounded-md p-4 md:p-5 grid grid-cols-3 md:grid-cols-6 gap-3"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 12px 30px rgba(12,42,64,0.06)",
      }}
      data-testid="portal-perf-kpi-row"
    >
      {cards.map((c) => (
        <div key={c.label} className="flex flex-col items-start">
          <div
            className="font-mono uppercase"
            style={{
              fontSize: "0.58rem",
              letterSpacing: "0.22em",
              color: "rgba(12,42,64,0.55)",
            }}
          >
            {c.label}
          </div>
          <div
            className="font-display mt-1"
            style={{
              fontSize: "1.7rem",
              lineHeight: 1,
              fontWeight: 600,
              color: c.color,
              letterSpacing: "-0.02em",
            }}
          >
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// -- Parameter dashboard table -------------------------------------------
function PerfParameterTable() {
  // Fully generic — no client / site name anywhere.
  const rows = [
    { p: "RO 1 Pre-treatment pressure", u: "Bar", t: "2–4",   d: [2.5, 3.7, 2.5, 2.7, "—"], flag: "ok" },
    { p: "RO 1 Inlet pressure",         u: "Bar", t: "10–12", d: [10.5, 11, 10.5, 10.5, "—"], flag: "ok" },
    { p: "RO 1 Permeate flow",          u: "LPM", t: "29–33", d: [34,  31, 30,  32,  "—"], flag: "warn" },
    { p: "RO 1 TDS",                    u: "PPM", t: "≤ 30",  d: [17.09, 16.8, 15.99, 18.94, "—"], flag: "ok" },
    { p: "RO 1 Recovery",               u: "%",   t: "≥ 60",  d: [68, 62, 60, 61.5, "—"], flag: "ok" },
    { p: "RO 2 Concentrate flow",       u: "LPM", t: "18–26", d: [16, 19, 20, 20, "—"], flag: "alarm" },
  ];
  const days = ["08-05", "08-06", "08-07", "08-08", "08-09"];
  const dot = (flag) => ({
    ok: "#1F9D63",
    warn: "#C99A2B",
    alarm: "#B85C38",
  }[flag] || "#1F9D63");

  return (
    <div
      className="rounded-md overflow-hidden"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 22px 45px rgba(12,42,64,0.10)",
      }}
      data-testid="portal-perf-parameter-table"
    >
      {/* window chrome */}
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ borderBottom: "1px solid rgba(12,42,64,0.08)", background: "#F5F5F0" }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#E06A5C" }} />
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#E3B34F" }} />
        <span style={{ width: 8, height: 8, borderRadius: 999, background: "#5AB56A" }} />
        <span
          className="ml-3 font-mono uppercase"
          style={{ fontSize: "0.6rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}
        >
          Portal · Plant performance · Last 5 days
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left" style={{ fontSize: "0.82rem", minWidth: 640 }}>
          <thead>
            <tr style={{ background: "#FAFAF7", color: "rgba(12,42,64,0.65)" }}>
              <th className="px-4 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>
                Parameter
              </th>
              <th className="px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>
                Unit
              </th>
              <th className="px-3 py-2.5 font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}>
                Target
              </th>
              {days.map((d) => (
                <th
                  key={d}
                  className="px-3 py-2.5 font-mono uppercase text-right"
                  style={{ fontSize: "0.6rem", letterSpacing: "0.16em" }}
                >
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.p} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}>
                <td className="px-4 py-2.5" style={{ color: "#0C2A40", fontWeight: 500 }}>
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      width: 8, height: 8, borderRadius: 999,
                      background: dot(r.flag),
                      marginRight: 8,
                    }}
                  />
                  {r.p}
                </td>
                <td className="px-3 py-2.5" style={{ color: "rgba(12,42,64,0.6)" }}>{r.u}</td>
                <td className="px-3 py-2.5" style={{ color: "rgba(12,42,64,0.6)" }}>{r.t}</td>
                {r.d.map((v, i) => (
                  <td
                    key={i}
                    className="px-3 py-2.5 text-right font-mono"
                    style={{
                      color: v === "—" ? "rgba(12,42,64,0.35)" : "#0C2A40",
                      fontSize: "0.82rem",
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -- Inline SVG trend chart ----------------------------------------------
function PerfTrendChart() {
  // Two overlaid line series across ~30 days — pure svg, no library.
  const w = 460, h = 210, pad = { l: 34, r: 14, t: 24, b: 26 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const seriesA = [10, 10.4, 10.5, 10.5, 10.4, 10.3, 10.2, 10.4, 10.6, 10.5, 10.4, 10.5, 10.7, 10.6, 10.5, 10.4, 10.3, 10.4, 10.5, 10.6, 10.7, 10.5, 10.4, 10.6, 10.8, 10.9, 10.7, 10.5, 10.5, 10.4];
  const seriesB = [2.5, 2.5, 2.5, 3.5, 3.0, 2.2, 1.8, 1.9, 2.4, 2.6, 2.8, 2.9, 2.8, 2.6, 2.5, 2.5, 2.6, 2.7, 2.8, 2.7, 2.6, 2.5, 2.4, 2.6, 2.8, 3.1, 3.5, 3.4, 2.9, 2.7];
  const minY = 0, maxY = 12;
  const scaleX = (i, len) => pad.l + (i / (len - 1)) * innerW;
  const scaleY = (v) => pad.t + innerH - ((v - minY) / (maxY - minY)) * innerH;
  const path = (arr) =>
    arr.map((v, i) => `${i === 0 ? "M" : "L"}${scaleX(i, arr.length).toFixed(1)},${scaleY(v).toFixed(1)}`).join(" ");
  const gridYs = [0, 3, 6, 9, 12];

  return (
    <div
      className="rounded-md p-4"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 12px 30px rgba(12,42,64,0.06)",
      }}
      data-testid="portal-perf-trend-chart"
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className="font-mono uppercase"
          style={{ fontSize: "0.62rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.6)" }}
        >
          Trend · RO 1 pressures · 30 days
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label="Trend chart preview">
        {gridYs.map((v) => (
          <g key={v}>
            <line
              x1={pad.l} x2={w - pad.r}
              y1={scaleY(v)} y2={scaleY(v)}
              stroke="rgba(12,42,64,0.08)"
            />
            <text
              x={pad.l - 6}
              y={scaleY(v) + 3}
              textAnchor="end"
              style={{ fontSize: 9, fill: "rgba(12,42,64,0.5)", fontFamily: "monospace" }}
            >
              {v}
            </text>
          </g>
        ))}
        <path d={path(seriesA)} fill="none" stroke="#1FB6C1" strokeWidth="2" />
        <path d={path(seriesB)} fill="none" stroke="#B85C38" strokeWidth="2" strokeDasharray="4 3" />
        <text x={pad.l} y={h - 8} style={{ fontSize: 9, fill: "rgba(12,42,64,0.5)", fontFamily: "monospace" }}>
          Day 0
        </text>
        <text x={w - pad.r - 26} y={h - 8} style={{ fontSize: 9, fill: "rgba(12,42,64,0.5)", fontFamily: "monospace" }}>
          Day 30
        </text>
      </svg>
      <div className="flex flex-wrap gap-4 mt-2" style={{ fontSize: "0.72rem" }}>
        <span className="flex items-center gap-2" style={{ color: "rgba(12,42,64,0.75)" }}>
          <span style={{ width: 14, height: 2, background: "#1FB6C1" }} />
          RO 1 Inlet pressure (Bar)
        </span>
        <span className="flex items-center gap-2" style={{ color: "rgba(12,42,64,0.75)" }}>
          <span style={{ width: 14, height: 2, background: "#B85C38", borderTop: "2px dashed #B85C38" }} />
          RO 1 Pre-treatment (Bar)
        </span>
      </div>
    </div>
  );
}

// -- Uptime card ---------------------------------------------------------
function PerfUptimeCard() {
  return (
    <div
      className="rounded-md p-5 flex flex-col justify-between"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 12px 30px rgba(12,42,64,0.06)",
      }}
      data-testid="portal-perf-uptime-card"
    >
      <div>
        <div
          className="font-mono uppercase mb-3"
          style={{ fontSize: "0.62rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.6)" }}
        >
          Plant uptime · last 30 days
        </div>
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 mb-5"
          style={{
            background: "rgba(31,157,99,0.10)",
            border: "1px solid rgba(31,157,99,0.35)",
            color: "#1F9D63",
            fontSize: "0.68rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#1F9D63" }} />
          Plant is currently RUNNING
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "Uptime", value: "100.00%", color: "#1F9D63" },
            { label: "Incidents", value: "0", color: "#0C2A40" },
            { label: "Downtime", value: "0s", color: "#0C2A40" },
            { label: "MTTR", value: "—", color: "rgba(12,42,64,0.55)" },
          ].map((k) => (
            <div key={k.label}>
              <div
                className="font-mono uppercase"
                style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
              >
                {k.label}
              </div>
              <div
                className="font-display mt-1"
                style={{ fontSize: "1.4rem", lineHeight: 1, fontWeight: 600, color: k.color, letterSpacing: "-0.02em" }}
              >
                {k.value}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="mt-5 pt-4 font-light"
        style={{ fontSize: "0.78rem", color: "rgba(26,31,37,0.6)", borderTop: "1px solid rgba(12,42,64,0.08)" }}
      >
        Uptime is derived from support tickets flagged &ldquo;plant down&rdquo; and matching
        &ldquo;plant back up&rdquo; confirmations — one number, auditable, board-ready.
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
//  OPS + ORDERING SECTION
//  Job-card pipeline · job-card detail · site-specific ordering storefront.
//  Everything is a generic "Water Treatment Plant" mock — no TGL / THOR /
//  Thornybush references, ever.
// -----------------------------------------------------------------------

const OPS_HIGHLIGHTS = [
  {
    icon: Wrench,
    title: "Every site visit — traced end-to-end",
    body:
      "From the moment a job card is opened to the moment the invoice is sent, the client sees the exact stage: created · picking / ordering parts · ready to invoice · invoice sent. Nothing lives in someone's inbox.",
  },
  {
    icon: CheckCircle2,
    title: "Tasks, photos and technician sign-off",
    body:
      "Each job card carries its brief, the assigned team, every completed task with before / after photos, and the lead technician's signature. Auditable — for you, your insurer and your compliance officer.",
  },
];

function OpsOrderingSection() {
  return (
    <section
      className="px-6 md:px-12 lg:px-20 py-14 md:py-24 relative overflow-hidden"
      style={{ background: "#FFFFFF", color: "#0C2A40" }}
      data-testid="portal-ops"
    >
      <div className="max-w-[1400px] mx-auto">
        <div
          className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
          style={{
            background: "rgba(12,42,64,0.06)",
            border: "1px solid rgba(12,42,64,0.14)",
            color: "#0C2A40",
            fontSize: "0.66rem",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontWeight: 700,
            borderRadius: 999,
          }}
        >
          <Wrench size={12} aria-hidden style={{ color: "#1FB6C1" }} />
          Job cards · site ordering
        </div>
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left column — copy */}
          <div className="lg:col-span-5">
            <h2
              className="font-display mb-5"
              style={{
                fontSize: "clamp(1.9rem, 3.6vw, 2.9rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.018em",
                fontWeight: 500,
                color: "#0C2A40",
              }}
            >
              Every service, every reorder<br />
              <span style={{ color: "#1FB6C1" }}>— traced against your site.</span>
            </h2>
            <p
              className="font-light text-[1rem] leading-relaxed mb-8"
              style={{ color: "rgba(26,31,37,0.72)", maxWidth: "48ch" }}
            >
              Job cards move through a visible pipeline. Tasks, technician
              photos and sign-off land against the site in real time. Reorders
              draw straight from the equipment already installed on the plant.
              Nothing filed on paper, nothing lost between technicians and
              accounts.
            </p>
            <ul className="space-y-5" data-testid="portal-ops-highlights">
              {OPS_HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-4">
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                    style={{ background: "rgba(31,182,193,0.12)", color: "#1FB6C1" }}
                  >
                    <Icon size={18} aria-hidden />
                  </div>
                  <div>
                    <div
                      className="font-display mb-1.5"
                      style={{
                        fontSize: "1.02rem",
                        color: "#0C2A40",
                        fontWeight: 600,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {title}
                    </div>
                    <p
                      className="font-light text-[0.92rem] leading-relaxed"
                      style={{ color: "rgba(26,31,37,0.72)" }}
                    >
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right column — 2 mockups stacked */}
          <div className="lg:col-span-7 space-y-5">
            <JobCardsPipelineMockup />
            <JobCardDetailMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

// -- Job cards pipeline (compact stage list) -----------------------------
function JobCardsPipelineMockup() {
  const stages = [
    {
      label: "Job card created", count: 1, tint: "#C99A2B",
      jobs: [{ id: "JC-0626", title: "Pre-treat pump leak", progress: 0 }],
    },
    {
      label: "Picking / ordering", count: 1, tint: "#C99A2B",
      jobs: [{ id: "JC-0578", title: "Pump service", when: "20 Jul, 12:00", progress: 0 }],
    },
    {
      label: "Ready to invoice", count: 1, tint: "#C99A2B",
      jobs: [{ id: "JC-0613", title: "RO valve replacement", when: "23 Jul, 07:00", progress: 100 }],
    },
    {
      label: "Invoice sent", count: 3, tint: "#1F9D63",
      jobs: [
        { id: "JC-0593", title: "AFM CIP & plant upgrade", when: "3 Jul, 05:00", progress: 100 },
        { id: "JC-0575", title: "Chemical delivery + RO 1", when: "8 Jun, 15:35", progress: 100 },
        { id: "JC-0565", title: "Pre-filtration tank overflow", when: "20 May, 11:30", progress: 100 },
      ],
    },
  ];
  return (
    <div
      className="rounded-md overflow-hidden"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 22px 45px rgba(12,42,64,0.10)",
      }}
      data-testid="portal-ops-jobcards-pipeline"
    >
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ borderBottom: "1px solid rgba(12,42,64,0.08)", background: "#F5F5F0" }}
      >
        <Wrench size={13} aria-hidden style={{ color: "#1FB6C1" }} />
        <div
          className="font-display"
          style={{ fontSize: "0.9rem", fontWeight: 600, color: "#0C2A40" }}
        >
          Job cards
        </div>
        <span
          className="ml-1 font-mono"
          style={{
            fontSize: "0.6rem",
            letterSpacing: "0.16em",
            color: "rgba(12,42,64,0.55)",
            padding: "2px 7px",
            border: "1px solid rgba(12,42,64,0.16)",
            borderRadius: 999,
          }}
        >
          10 OPEN
        </span>
      </div>
      <div className="divide-y" style={{ borderColor: "rgba(12,42,64,0.06)" }}>
        {stages.map((s) => (
          <div key={s.label} className="px-4 py-3">
            <div className="flex items-center gap-3 mb-2">
              <span
                className="font-mono uppercase"
                style={{
                  fontSize: "0.6rem",
                  letterSpacing: "0.2em",
                  color: s.tint,
                  padding: "2px 9px",
                  border: `1px solid ${s.tint}55`,
                  borderRadius: 999,
                  background: `${s.tint}11`,
                  fontWeight: 700,
                }}
              >
                {s.label}
              </span>
              <span
                className="font-mono"
                style={{ fontSize: "0.68rem", color: "rgba(12,42,64,0.55)" }}
              >
                {s.count} {s.count === 1 ? "job" : "jobs"}
              </span>
            </div>
            <div className="space-y-2">
              {s.jobs.map((j) => (
                <div
                  key={j.id}
                  className="rounded-sm px-3 py-2 flex items-center gap-3 flex-wrap"
                  style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.06)" }}
                >
                  <span
                    className="font-mono"
                    style={{ fontSize: "0.68rem", color: "#1FB6C1", letterSpacing: "0.05em" }}
                  >
                    #{j.id}
                  </span>
                  <span
                    className="font-display"
                    style={{ fontSize: "0.86rem", color: "#0C2A40", fontWeight: 600 }}
                  >
                    {j.title}
                  </span>
                  {j.when && (
                    <span
                      className="font-mono"
                      style={{ fontSize: "0.66rem", color: "rgba(12,42,64,0.55)" }}
                    >
                      {j.when}
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2" style={{ minWidth: 120 }}>
                    <div
                      style={{
                        flex: 1, height: 5, background: "rgba(12,42,64,0.08)", borderRadius: 999,
                      }}
                    >
                      <div
                        style={{
                          width: `${j.progress}%`,
                          height: "100%",
                          background: j.progress === 100 ? "#1F9D63" : "#C99A2B",
                          borderRadius: 999,
                        }}
                      />
                    </div>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: "0.66rem",
                        color: j.progress === 100 ? "#1F9D63" : "#C99A2B",
                        fontWeight: 700,
                      }}
                    >
                      {j.progress}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -- Job card detail preview --------------------------------------------
function JobCardDetailMockup() {
  const tasks = [
    { n: 1, t: "Replace UF solenoid with 25 mm actuator", done: true, photos: 1 },
    { n: 2, t: "Replace blocked solenoid on WTP with 2\" actuator", done: true, photos: 0 },
    { n: 3, t: "Install EVP float & relay on CIP circuit", done: true, photos: 3 },
    { n: 4, t: "Bleed line and pressure-test panel", done: false, photos: 0 },
  ];
  return (
    <div
      className="rounded-md overflow-hidden"
      style={{
        background: "#FFFFFF",
        border: "1px solid rgba(12,42,64,0.10)",
        boxShadow: "0 22px 45px rgba(12,42,64,0.10)",
      }}
      data-testid="portal-ops-jobcard-detail"
    >
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ borderBottom: "1px solid rgba(12,42,64,0.08)", background: "#F5F5F0" }}
      >
        <span className="font-mono" style={{ fontSize: "0.66rem", color: "#1FB6C1", letterSpacing: "0.05em" }}>
          #JC-0613
        </span>
        <div className="font-display" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#0C2A40" }}>
          RO valve replacement
        </div>
        <span
          className="ml-auto font-mono uppercase"
          style={{
            fontSize: "0.6rem",
            letterSpacing: "0.18em",
            color: "#1F9D63",
            padding: "2px 8px",
            border: "1px solid rgba(31,157,99,0.35)",
            background: "rgba(31,157,99,0.10)",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          Ready to invoice
        </span>
      </div>
      <div className="p-4 grid sm:grid-cols-3 gap-3">
        {[
          { l: "Scheduled", v: "Thu 23 Jul · 07:00" },
          { l: "Site", v: "Water Treatment Plant" },
          { l: "Progress", v: "100 %", tone: "#1F9D63" },
        ].map((f) => (
          <div key={f.l}>
            <div
              className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}
            >
              {f.l}
            </div>
            <div
              className="font-display mt-1"
              style={{ fontSize: "0.86rem", fontWeight: 600, color: f.tone || "#0C2A40" }}
            >
              {f.v}
            </div>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4 pt-1">
        <div
          className="font-mono uppercase mb-2"
          style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
        >
          Brief
        </div>
        <p className="font-light" style={{ fontSize: "0.9rem", color: "rgba(26,31,37,0.75)" }}>
          New valves to be tested on site — check pressure hold across the RO array
          and confirm no bypass on the concentrate line.
        </p>
      </div>
      <div className="px-4 pb-4">
        <div
          className="font-mono uppercase mb-2 flex items-center justify-between"
          style={{ fontSize: "0.58rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.55)" }}
        >
          <span>Tasks · {tasks.length}</span>
          <span style={{ color: "#1F9D63" }}>3 of 4 done</span>
        </div>
        <div className="space-y-2">
          {tasks.map((tk) => (
            <div
              key={tk.n}
              className="rounded-sm px-3 py-2 flex items-center gap-3"
              style={{ background: "#FAFAF7", border: "1px solid rgba(12,42,64,0.06)" }}
            >
              <span
                className="font-mono shrink-0 flex items-center justify-center"
                style={{
                  width: 22, height: 22, borderRadius: 4,
                  background: tk.done ? "rgba(31,157,99,0.12)" : "rgba(12,42,64,0.06)",
                  color: tk.done ? "#1F9D63" : "rgba(12,42,64,0.55)",
                  fontSize: "0.68rem",
                  fontWeight: 700,
                }}
              >
                {tk.done ? "✓" : tk.n}
              </span>
              <div style={{ fontSize: "0.86rem", color: "#0C2A40", flex: 1 }}>
                {tk.t}
              </div>
              {tk.photos > 0 && (
                <span
                  className="font-mono"
                  style={{
                    fontSize: "0.6rem",
                    letterSpacing: "0.16em",
                    color: "rgba(12,42,64,0.6)",
                    padding: "2px 7px",
                    border: "1px solid rgba(12,42,64,0.14)",
                    borderRadius: 4,
                  }}
                >
                  {tk.photos} PHOTO{tk.photos > 1 ? "S" : ""}
                </span>
              )}
              {tk.done && (
                <span
                  className="font-mono uppercase"
                  style={{
                    fontSize: "0.58rem", letterSpacing: "0.18em", color: "#1F9D63", fontWeight: 700,
                  }}
                >
                  Done
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -- Site-specific storefront preview -----------------------------------
// (Removed — client already has the equipment-aware ordering shown as
//  the "Orders" screen in the Screen Tour section further down the page.)

